import { WorkflowOverview } from "~/organisms/WorkflowOverview";
import { DocumentMetadata } from "~/molecules/DocumentMetadata";
import { DocumentReaderConfirmation } from "~/molecules/DocumentReaderConfirmation";
import { useTranslation } from "react-i18next";
import type {
  DocumentDetails,
  DocumentDetailsVersion,
  User,
} from "~/api/types";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useWorkflow } from "~/api/v2/useWorkflow";
import { useReaderReport } from "~/api/v2/useReaderReport";
import { DiffSummary, useDiffSummaryVisible } from "~/molecules/DiffSummary";
import {
  canSeeAnchoringTab,
  canSeeWorkflowTab,
  isPublishedVersion,
} from "~/helpers/documentPermissions";
import { CenterSpinner } from "~/atoms/spinner";
import ErrorState from "~/molecules/ErrorState";
import { TabId, resolveTabState } from "~/molecules/DocumentPanelTabsState";
import { ReadersOverviewTab } from "~/organisms/ReadersOverviewTab";
import { PanelTabs, type PanelTab } from "~/molecules/PanelTabs";

interface DocumentPanelTabsProps {
  user: User;
  selectedVersion: DocumentDetailsVersion;
  document: DocumentDetails;
  onChange: () => void;
  onConfetti: () => void;
}

export const DocumentPanelTabs = ({
  user,
  selectedVersion,
  document,
  onChange,
  onConfetti,
}: DocumentPanelTabsProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParamsFromHook] = useSearchParams();

  const {
    data: workflow,
    mutate: mutateWorkflow,
    error: workflowError,
    isLoading: isWorkflowLoading,
  } = useWorkflow(selectedVersion?.workflowId);
  const {
    data: readerReport,
    mutate: mutateReaderReport,
    error: readerReportError,
    isLoading: isReaderReportLoading,
  } = useReaderReport(selectedVersion?.id);

  const currentReader = readerReport?.readers.find((r) => r.userId === user.id);
  const isReader = currentReader !== undefined && !document.archivedAt;
  const hasRead = currentReader?.hasRead ?? false;

  const diffSummaryResult = useDiffSummaryVisible(
    selectedVersion,
    document.versions,
  );

  const showWorkflowTab = canSeeWorkflowTab(user.id, selectedVersion, workflow);
  const showReadingTab = isReader && isPublishedVersion(selectedVersion);
  const showAnchoringTab = canSeeAnchoringTab(selectedVersion);

  const handleChange = () => {
    void mutateWorkflow();
    onChange();
  };

  const handleWorkflowPublish = (versionId: number) => {
    handleChange();
    setSearchParamsFromHook((prev) => {
      const next = new URLSearchParams(prev);
      next.set("versionId", String(versionId));
      return next;
    });
    onConfetti();
  };

  const tabs: (PanelTab | null)[] = [
    showReadingTab
      ? {
          id: TabId.reading,
          label: t(($) => $.commons.workflow.reading),
          content: (
            <DocumentReaderConfirmation
              documentVersionId={selectedVersion.id}
              onConfirm={() => void mutateReaderReport()}
              isDone={hasRead}
            />
          ),
          badge: !hasRead && (
            <span className="bg-primary absolute -top-1 -right-1 size-3 animate-pulse rounded-full shadow-[0_0_10px_var(--primary)]" />
          ),
        }
      : null,
    showAnchoringTab
      ? {
          id: TabId.anchoring,
          label: t(($) => $.commons.workflow.anchoring),
          content: (
            <ReadersOverviewTab
              documentVersion={selectedVersion}
              category={document.category}
            />
          ),
        }
      : null,
    showWorkflowTab
      ? {
          id: TabId.workflow,
          label: t(($) => $.commons.workflows.workflow, { count: 1 }),
          content: (
            <WorkflowOverview
              workflow={workflow}
              selectedVersion={selectedVersion}
              onPublish={handleWorkflowPublish}
              onChange={handleChange}
            />
          ),
        }
      : null,
    diffSummaryResult.diffSummaryVisible
      ? {
          id: TabId.summary,
          label: t(($) => $.commons.workflow.diffSummary.tabTitle),
          content: (
            <DiffSummary
              documentId={document.id}
              versionId={selectedVersion.id}
              fileUpdatedAt={selectedVersion.fileUpdatedAt}
              previousVersionInfo={diffSummaryResult.previousVersionInfo}
            />
          ),
        }
      : null,
    {
      id: TabId.metadata,
      label: t(($) => $.commons.document.metadata),
      content: (
        <DocumentMetadata
          document={document}
          documentVersionId={selectedVersion.id}
          onSave={handleChange}
        />
      ),
    },
  ];

  const visibleTabs = tabs.filter((tab): tab is PanelTab => tab !== null);
  const tabFromUrl = searchParams.get("tab");
  const visibleTabIdsInDisplayOrder = visibleTabs.map((tab) => tab.id);
  const { activeTab, shouldSyncUrlTab } = resolveTabState({
    tabFromUrl,
    visibleTabIdsInDisplayOrder,
  });

  useEffect(() => {
    if (!shouldSyncUrlTab) {
      return;
    }

    setSearchParamsFromHook(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", activeTab);
        return next;
      },
      { replace: true },
    );
  }, [activeTab, setSearchParamsFromHook, shouldSyncUrlTab]);

  const handleTabChange = (value: string) => {
    setSearchParamsFromHook((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    });
  };

  if (isWorkflowLoading || isReaderReportLoading) {
    return <CenterSpinner />;
  }

  if (workflowError || readerReportError) {
    return <ErrorState />;
  }

  return (
    <PanelTabs
      tabs={visibleTabs}
      value={activeTab}
      onValueChange={handleTabChange}
      className="pt-4"
    />
  );
};
